import type { CSSProperties, ReactNode } from 'react'
import { useState, useSyncExternalStore } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { ChevronDown, ChevronLeft, ChevronRight, ChevronUp, Facebook, Info, Instagram, Linkedin, Mail, X } from 'lucide-react'
import { bringInfoBarToFront, subscribeOverlayStack, getOverlayTop } from '../../lib/roads/overlayStack'
import { getMapTheme, subscribeMapTheme } from '../../lib/roads/theme'
import { useRoadStore, type BikeType } from '@/lib/roads/store'
import BikeTypeTip from '@/components/ui/BikeTypeTip'
import SuggestRoad from './SuggestRoad'

const DOTS = [
  { color: '#FF4438', label: 'Sport', img: '/images/sport-outline.png' },
  { color: '#34D26B', label: 'ADV', img: '/images/adv-outline.png' },
  { color: '#FF9F1C', label: 'Sumo', img: '/images/sumo-outline.png' },
  { color: '#3B82F6', label: 'Cruiser', img: '/images/cruiser-outline.png' },
]

const SECTIONS = [
  {
    title: 'About RoadRatings',
    body: (
      <>
        <p className="text-base leading-relaxed text-white/80 font-bold pb-3">
          The goal is to show all roads worth riding with a road-going motorcycle, along with a rating and description of each road.
        </p>
        <p className="text-sm leading-relaxed text-white/60 italic pb-3">
          The perfect road is mile after mile of clean, dry, and smooth black tarmac undulating over and through gorgeous scenery with no traffic, offering up a variety of twists and turns.
        </p>
        <p className="text-sm leading-relaxed text-white/60">
          Sometimes a road makes it on the list purely because it is a convenient road to take when connecting two other roads, or because I happened to ride it and it has some redeeming features.
        </p>
      </>
    ),
  },
  {
    title: 'About the Ratings',
    body: (
      <>
      <p className="text-base leading-relaxed text-white/70 pb-2 font-bold">
        Roads are rated in the following categories, with lower ratings being given when the quality of the ride is compromised.
      </p>
      <table className="w-full text-sm border-collapse">
        <tbody>
          {[
            ['Fun', 'Primarily to do with the layout of the road itself. How twisty is it? Do the turns flow together? Is it more enjoyable or more nerve-wracking? Views, scenery, and tunnels are also part of this rating'],
            ['Road Condition', 'The quality of the road surface. Is it asphalt or concrete? Are there tar snakes? Pot holes and cracks? Is it bumpy or smooth? "How much do the road surface imperfections impede the flow of a spirited ride?"'],
            ['Dirt / Debris', 'How much crap is on the road? Dirt, leaves, gravel, snow, water, etc. "How much does the crap on the road impede the flow of a spirited ride?"'],
            ['Traffic', 'How much traffic is on the road? Availability of pull-offs and passing zones plays into this. "How much does traffic impede the flow of a spirited ride?"'],
          ].map(([label, text]) => (
            <tr key={label} className="border-b border-white/10 last:border-b-0">
              <td className="align-top py-2 pr-3 whitespace-nowrap font-semibold text-white/80 w-[1%]">{label} =</td>
              <td className="align-top py-2 text-white/60 leading-relaxed">{text}</td>
            </tr>
          ))}
        </tbody>
      </table>
      </>
    ),
  },
  {
    title: 'About the Ride Types',
    body: (
      <>
      <p className="text-base leading-relaxed text-white/70 pb-2 font-bold">
        Roads are also rated for a suggested motorcycle style
      </p>
      <p className="text-sm leading-relaxed text-white/60 pb-3 italic">
        "What bike do I want to be on to really enjoy this road?"
      </p>
      <table className="w-full text-sm border-collapse">
        <tbody>
          {[
            ['Sport', 'smooth road with medium to high speed turns'],
            ['Sumo', 'smooth or rough road with primarily tight turns'],
            ['ADV', 'rough road, possibly with some dirt, medium to high speed turns'],
            ['Cruiser', 'smooth road with gentle turns and scenic views'],
          ].map(([label, text]) => (
            <tr key={label} className="border-b border-white/10 last:border-b-0">
              <td className="align-top py-2 pr-3 whitespace-nowrap font-semibold text-white/80 w-[1%]">{label} =</td>
              <td className="align-top py-2 text-white/60 leading-relaxed">{text}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="text-base leading-relaxed text-white/70 pt-3 font-bold">
        If multiple bike styles are suggested (e.g. Sumo, ADV, Sport), then the first listed (Sumo) is the bike best suited to the road, and the others listed (ADV, Sport) would also be well suited.
      </p>
      </>
    ),
  },
  {
    title: 'About the Rater (Rudy)',
    body: (
      <>
        <p className="text-sm leading-relaxed text-white/60 pb-3">
          Rudy’s first brush with two wheels came at age eight, clinging to the back of his mom’s Harley-Davidson Sportster. The seed was planted. About ten years later, he borrowed her Vespa to get to work. Yet barely a mile from the house, he was grinning so hard he knew: the first chance I get, I’m getting my own bike.
        </p>
        <p className="text-sm leading-relaxed text-white/60 pb-3">
          That chance came in college, when several co-op paychecks turned into a shiny 2008 Ninja 250. Rudy cut his teeth carving the mountains of Tennessee, and from then on he’s always had a bike…or a few. His garage has been a revolving door of old, broken machines he’d fix, ride, and sell for a profit. Twenty-one bikes and roughly 275,000 miles later, he’s ridden across most of the country. The crown jewel? A two-month, 11,000-mile double cross-country trip on a 2008 KLR650 in 2015. A couple of Iron Butts too (1k miles in 20 hours, 1.3k miles in 23.5 hours).
        </p>
        <p className="text-sm leading-relaxed text-white/60 pb-3">
          The track bug bit in 2014 while he was in grad school in Colorado, then bit harder after moving to California in 2016. By 2021 he was racing with AFM and Supermoto USA West, adding CRA to the mix in 2025.
        </p>
        <p className="text-sm leading-relaxed text-white/60 pb-3">
          Rudy’s no stranger to teaching, either. He’s been doing it since age 17, starting with math and English at Kumon, later adding chemistry and physics tutoring through undergrad and grad school. In 2021, he launched a bachata dance company that he ran full-time for 3 years until injuries took him out of the dance scene. In the riding world, he began teaching privately in 2017 and wrenching in 2018, ramping up in earnest by 2024. He’s now clocked 18 years of teaching and 11 years of wrenching experience, including a stint as a mechanic at The Garage of Silicon Valley. Plus, he got his MSF teaching certification in 2025.
        </p>
        <p className="text-sm leading-relaxed text-white/60 pb-3">
          And when he’s not touring, tracking, racing, wrenching, or teaching? He’s geeking out over twisty-road data. In 2017, Rudy started cataloging and rating every road he’d ridden, plus every road he wanted to ride. That project grew into RoadRatings.com, an attempt to list every paved, twisty road in America (and eventually the world!), with Rudy aiming to personally ride and rate them all.
        </p>
        <p className="text-sm leading-relaxed text-white/60 pb-3">
          Multiple developer and investor pitches later, the app is still waiting for the right team to bring it to life. If that sounds like your kind of project, Rudy is always happy to talk shop.
        </p>
        <p className="text-sm leading-relaxed text-white/80 font-semibold">
          <a href="mailto:Rudy@RoadRatings.com" className="text-amber-400 hover:text-amber-300 underline underline-offset-2">
            Rudy@RoadRatings.com
          </a>
        </p>
      </>
    ),
  },
  {
    title: 'Contact',
    body: (
      <div className="flex items-center justify-center gap-6 py-2">
        {[
          { href: 'mailto:roadratings@gmail.com', label: 'Email', icon: <Mail size={28} /> },
          { href: 'https://instagram.com/roadratingsmoto', label: 'Instagram', icon: <Instagram size={28} /> },
          { href: 'https://x.com/roadratings', label: 'X', icon: <XIcon size={28} /> },
          { href: 'https://www.facebook.com/RoadRatings/', label: 'Facebook', icon: <Facebook size={28} /> },
          { href: 'https://www.linkedin.com/company/roadratings', label: 'LinkedIn', icon: <Linkedin size={28} /> },
        ].map((item) => (
          <a
            key={item.label}
            href={item.href}
            target={item.href.startsWith('mailto:') ? undefined : '_blank'}
            rel="noreferrer"
            aria-label={item.label}
            title={item.label}
            className="text-amber-400 hover:text-amber-300 transition-colors"
          >
            {item.icon}
          </a>
        ))}
      </div>
    ),
  },
]

function XIcon({ size = 24 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  )
}

function LegendHorizontal({ dark }: { dark: boolean }) {
  const filters = useRoadStore((s) => s.filters)
  const setFilters = useRoadStore((s) => s.setFilters)

  const toggleBike = (type: BikeType) => {
    const has = filters.bikeTypes.includes(type)
    setFilters({
      bikeTypes: has ? filters.bikeTypes.filter((b) => b !== type) : [...filters.bikeTypes, type],
    })
  }

  return (
    <div className="flex flex-row justify-center items-start gap-6 sm:gap-8">
      {DOTS.map((d) => {
        const active = filters.bikeTypes.includes(d.label as BikeType)
        return (
          <BikeTypeTip key={d.label} type={d.label}>
            <button
              type="button"
              onClick={() => toggleBike(d.label as BikeType)}
              aria-pressed={active}
              aria-label={`${active ? 'Disable' : 'Enable'} ${d.label} filter`}
              className={`flex flex-col items-center gap-[2.5px] cursor-pointer rounded-lg p-1 transition-shadow ${
                active ? 'ring-1 ring-yellow-300 ring-inset' : ''
              }`}
            >
              <img src={d.img} alt="" className="h-12 w-12 object-contain" />
              <span
                className={`text-[16px] font-mono uppercase tracking-wider ${
                  dark ? 'text-white' : 'text-white/90'
                }`}
              >
                {d.label}
              </span>
              <span className="w-12 h-[3px] rounded-full" style={{ background: d.color }} />
            </button>
          </BikeTypeTip>
        )
      })}
    </div>
  )
}

function AboutOverlay({ dark, onClose }: { dark: boolean; onClose: () => void }) {
  const [openIdx, setOpenIdx] = useState<number | null>(0)

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className={`fixed inset-0 z-[75] backdrop-blur-md ${
        dark ? 'bg-[#0A0A0B]/98' : 'bg-[#2A2A2C]/90'
      }`}
      onClick={onClose}
    >
      <button
        onClick={onClose}
        aria-label="Close"
        className={`absolute top-4 right-4 z-10 h-9 w-9 rounded-lg border flex items-center justify-center ${
          dark
            ? 'border-white/15 text-white/60 hover:text-white hover:border-white/30'
            : 'border-white/20 text-white/70 hover:text-white hover:border-white/40'
        }`}
      >
        <X size={18} />
      </button>

      <div className="h-full flex flex-col">
        <div className={`shrink-0 pt-4 pb-2 px-6 border-b ${dark ? 'border-white/10' : 'border-white/10'}`}>
          <h2
            className={`text-center text-xl font-bold tracking-tight mb-3 ${
              dark ? 'text-white' : 'text-white'
            }`}
            style={{ WebkitTextStroke: dark ? '1px black' : '1px #3A3A3C', paintOrder: 'stroke fill' } as CSSProperties}
          >
            Color shows the kind of ride.
            <br />
            Intensity shows how good it is.
          </h2>
          <LegendHorizontal dark={dark} />
          <div
            className={`mx-auto mt-4 w-64 h-2 rounded-full bg-gradient-to-r from-amber-400 ${
              dark ? 'via-white/60 to-white/20' : 'via-white/60 to-white/20'
            }`}
          />
          <div
            className={`flex justify-between w-64 mx-auto text-[11px] font-mono uppercase tracking-wider ${
              dark ? 'text-white/70' : 'text-white/70'
            }`}
          >
            <span>vivid · best</span>
            <span>muted · average</span>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 pt-6 pb-32">
          <div
            className="max-w-2xl mx-auto space-y-3"
            onClick={(e) => e.stopPropagation()}
          >
            {SECTIONS.map((s, i) => {
              const open = openIdx === i
              return (
                <div
                  key={s.title}
                  className={`rounded-xl border overflow-hidden ${
                    dark ? 'bg-[#0C0C0E] border-white/10' : 'bg-[#48484A] border-white/10'
                  }`}
                >
                  <button
                    onClick={() => setOpenIdx(open ? null : i)}
                    className="w-full min-h-11 px-4 py-3 flex items-center justify-between text-left"
                  >
                    <span className="text-sm font-bold tracking-widest uppercase text-amber-400">
                      {s.title}
                    </span>
                    <span className="text-amber-400">
                      {open ? <ChevronLeft size={16} /> : <ChevronRight size={16} />}
                    </span>
                  </button>
                  <AnimatePresence initial={false}>
                    {open && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                      >
                        <div className="px-4 pb-4 text-sm leading-relaxed text-white/60">
                          {s.body}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </motion.div>
  )
}

export default function InfoBar() {
  const [pulled, setPulled] = useState(false)
  const [aboutOpen, setAboutOpen] = useState(false)
  const dark = useSyncExternalStore(subscribeMapTheme, getMapTheme) === 'dark'
  const onTop = useSyncExternalStore(subscribeOverlayStack, getOverlayTop) === 'infobar'

  return (
    <>
      <div className={`fixed bottom-3 inset-x-0 flex justify-center pointer-events-none ${onTop ? 'rudy-infobar-on-top' : 'z-[15]'}`}>
        <div
          className={`pointer-events-auto flex flex-col items-stretch rounded-xl backdrop-blur border-2 shadow-lg overflow-hidden ${
            dark ? 'bg-[#0C0C0E]/95 border-white' : 'bg-[#3A3A3C]/95 border-white'
          }`}
        >
          <button
            onClick={() => { bringInfoBarToFront(); setPulled((v) => !v) }}
            aria-label={pulled ? 'Collapse info panel' : 'Expand info panel'}
            className="w-full min-h-7 min-w-[292px] px-16 flex items-center justify-center text-amber-400 hover:text-amber-300"
          >
            {pulled ? <ChevronDown size={18} strokeWidth={3.5} /> : <ChevronUp size={18} strokeWidth={3.5} />}
          </button>

          <AnimatePresence initial={false}>
            {pulled && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.25 }}
                className="overflow-hidden"
              >
                <div className="flex flex-col items-stretch gap-3 px-3 pt-1 pb-2 sm:flex-row sm:items-center sm:gap-4">
                  <button
                    onClick={() => { bringInfoBarToFront(); setAboutOpen(true) }}
                    className="order-2 w-full min-h-11 h-11 px-3 rounded-lg bg-amber-400 hover:bg-amber-300 text-black text-sm font-semibold flex items-center justify-center gap-2 whitespace-nowrap sm:order-none sm:w-[160px] sm:shrink-0"
                  >
                    <Info size={14} className="shrink-0" />
                    <span>About</span>
                  </button>

                  <div className="order-1 flex-1 flex justify-center sm:order-none">
                    <LegendHorizontal dark={dark} />
                  </div>

                  <div
                    className="order-3 w-full flex sm:order-none sm:w-[160px] sm:shrink-0 sm:ml-auto"
                    onPointerDown={bringInfoBarToFront}
                  >
                    <SuggestRoad />
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      <AnimatePresence>
        {aboutOpen && <AboutOverlay dark={dark} onClose={() => setAboutOpen(false)} />}
      </AnimatePresence>
    </>
  )
}
